import * as THREE from 'three';
import { LinesAA, LINE_PRESETS, type LineSegment } from '@arkturian/three-kit/lines';
import { Vector3 } from '../types/Vector3';
import { Color } from '../types/Color';
import { InterpolatedProperty } from '../properties/InterpolatedProperty';

export interface CurveStyle {
    thickness: number;
    color: Color;
    opacity: number;
    segments: number;
    material: 'basic' | 'standard' | 'glow';
}

export interface CurvePoint {
    position: Vector3;
    thickness?: number;  // Per-point thickness variation
    color?: Color;       // Per-point color variation
}

export interface ProcessMarkerStyle {
    showNumbers: boolean;
    numberColor: Color;
    markerSize: number;          // world units scale for sprites
    fontFamily: string;
    fontWeight: string;
    fontSizePx: number;          // canvas pixel font size
    showStartMarker: boolean;
    startText: string;
    showEndMarker: boolean;
    endText: string;
    lineOffset?: number;         // offset along line towards next/prev (0..1 fraction of segment)
    preOffset: number;           // extend line before first POI (world units)
    postOffset: number;          // extend line after last POI (world units)
}

/**
 * Universal morphable curve system with smooth interpolation
 * Each vertex is an InterpolatedProperty for buttery-smooth morphing
 */
export class MorphableCurve {
    private static readonly DEFAULT_VERTEX_COUNT = 256;
    
    // Core properties
    private _vertices: InterpolatedProperty<Vector3>[];
    private _scene: THREE.Scene | null = null;
    private _curveGroup: THREE.Group;
    /** Kernlinie. Persistent — setLines() schreibt in vorallokierte GPU-Puffer. */
    private _linesAA: LinesAA | null = null;
    /** Weicher Halo hinter der Kernlinie, nur im 'bold'-Stil. */
    private _haloAA: LinesAA | null = null;
    private _markersGroup: THREE.Group;
    private _numberSprites: THREE.Sprite[] = [];
    private _startSprite: THREE.Sprite | null = null;
    private _endSprite: THREE.Sprite | null = null;
    private _lastAnchorPoints: Vector3[] = [];
    private _lastExtendedPoints: Vector3[] = [];
    
    // Style properties
    private _style: CurveStyle = {
        thickness: 0.1,
        color: new Color('#ffffff'),
        opacity: 1.0,
        segments: 16,
        material: 'basic'
    };
    private _markerStyle: ProcessMarkerStyle = {
        showNumbers: true,
        numberColor: new Color('#fff5ee'),
        markerSize: 0.42,
        fontFamily: 'Georgia, serif',
        fontWeight: '600',
        fontSizePx: 64,
        showStartMarker: true,
        startText: 'S',
        showEndMarker: true,
        endText: 'E',
        lineOffset: -0.3,
        preOffset: 3,
        postOffset: 3
    };
    
    // State
    private _visible: boolean = true;
    /**
     * Optionale Nachbearbeitung der fertig geglaetteten+resampelten Punkte —
     * z.B. Projektion auf eine Kugeloberflaeche bei sphaerischen Layouts,
     * damit die Linie der Oberflaeche folgt statt als Sehne durchs Innere.
     */
    private _pathPostProcessor: ((points: Vector3[]) => Vector3[]) | null = null;
    private _vertexCount: number;
    private _isAnimating: boolean = false;
    private _animationFrameId: number | null = null;
    
    constructor(vertexCount: number = MorphableCurve.DEFAULT_VERTEX_COUNT) {
        this._vertexCount = vertexCount;
        this._curveGroup = new THREE.Group();
        this._curveGroup.name = 'MorphableCurve';
        this._markersGroup = new THREE.Group();
        this._markersGroup.name = 'MorphableCurveMarkers';
        this._curveGroup.add(this._markersGroup);
        
        // Initialize interpolated vertices
        this._vertices = Array.from({length: vertexCount}, (_, i) => 
            new InterpolatedProperty<Vector3>(
                `vertex_${i}`, 
                new Vector3(0, 0, 0), 
                new Vector3(0, 0, 0),
                2 // Default animation duration
            )
        );
        
        //console.log(`✨ MorphableCurve: Created with ${vertexCount} interpolated vertices`);
    }
    
    /**
     * Set the scene for rendering
     */
    setPathPostProcessor(fn: ((points: Vector3[]) => Vector3[]) | null): void {
        this._pathPostProcessor = fn;
    }

    setScene(scene: THREE.Scene): void {
        if (this._scene && this._scene !== scene) {
            this._scene.remove(this._curveGroup);
        }
        
        this._scene = scene;
        if (this._scene) {
            this._scene.add(this._curveGroup);
        }
    }
    
    /**
     * Morph curve to new path with smooth animation
     */
    morphToPath(controlPoints: Vector3[], duration: number = 1.0): void {
        //console.log(`🌊 MorphableCurve: Morphing to ${controlPoints.length} control points over ${duration}s`);
        
        if (controlPoints.length < 2) {
            console.warn('⚠️ MorphableCurve: Need at least 2 control points');
            return;
        }
        
        // Optionally extend path before first and after last point
        const extendedPoints = this.extendPathWithOffsets(controlPoints, this._markerStyle.preOffset, this._markerStyle.postOffset);
        this._lastAnchorPoints = controlPoints.map(p => p.clone());
        this._lastExtendedPoints = extendedPoints.map(p => p.clone());
        
        // Generate smooth curve from extended control points
        const smoothCurve = this.generateSmoothCurve(extendedPoints);
        
        // Resample to fixed vertex count for perfect morphing
        let resampledPoints = this.resampleCurve(smoothCurve, this._vertexCount);
        if (this._pathPostProcessor) resampledPoints = this._pathPostProcessor(resampledPoints);
        
        // Animate each vertex independently - this is the magic! ✨
        this._vertices.forEach((vertex, i) => {
            vertex.setDuration(duration); // Set animation duration
            vertex.value = resampledPoints[i]; // Setting value triggers animation
        });
        
        // Ensure marker sprites exist and are positioned
        this.buildMarkers();
        this.positionMarkers();

        // Start continuous geometry updates during animation
        this.startAnimationUpdates();
    }
    
    /**
     * Set curve style (thickness, color, material)
     */
    setStyle(style: Partial<CurveStyle>): void {
        this._style = { ...this._style, ...style };
        //console.log(`🎨 MorphableCurve: Style updated - thickness: ${this._style.thickness}, color: ${this._style.color.toHex()}`);
        this.updateGeometry();
    }

    /**
     * Configure process marker style (numbers, start/end, offsets)
     */
    setMarkerStyle(style: Partial<ProcessMarkerStyle>): void {
        this._markerStyle = { ...this._markerStyle, ...style };
        // Rebuild and reposition markers with new style
        this.buildMarkers();
        this.positionMarkers();
    }
    
    /**
     * Start continuous animation updates
     */
    private startAnimationUpdates(): void {
        if (this._isAnimating) return; // Already animating
        
        this._isAnimating = true;
        //console.log(`🌊 MorphableCurve: Starting continuous animation updates`);
        
        const animationLoop = () => {
            if (!this._isAnimating) return;
            
            // Check if any vertex is still animating
            const hasAnimatingVertices = this._vertices.some(vertex => vertex.isInterpolating);
            
            if (hasAnimatingVertices) {
                // Update geometry with current vertex positions
                this.updateGeometry();
                
                // Continue animation loop
                this._animationFrameId = requestAnimationFrame(animationLoop);
            } else {
                // Animation finished
                this.stopAnimationUpdates();
                //console.log(`✨ MorphableCurve: Animation complete!`);
            }
        };
        
        // Start the loop
        animationLoop();
    }
    
    /**
     * Stop animation updates
     */
    private stopAnimationUpdates(): void {
        this._isAnimating = false;
        if (this._animationFrameId !== null) {
            cancelAnimationFrame(this._animationFrameId);
            this._animationFrameId = null;
        }
    }
    
    /**
     * Update curve geometry based on current vertex positions
     */
    private updateGeometry(): void {
        if (!this._scene) return;

        // Aktuelle Positionen der interpolierten Vertices.
        const currentPositions = this._vertices.map(vertex => {
            const pos = vertex.value || new Vector3(0, 0, 0);
            return new THREE.Vector3(pos.x, pos.y, pos.z);
        });
        if (currentPositions.length < 2) return;

        const rgb = new THREE.Color(this._style.color.r, this._style.color.g, this._style.color.b);
        const width = MorphableCurve.screenWidthPx();

        // Waves Kernprinzip (LINE_PRESETS.subtle): Strukturelemente stehen bei
        // gedämpfter Deckkraft zurück, nur der Inhalt ist voll kontrastig.
        // Multiplikativ, damit style.opacity weiterhin greift.
        const coreOpacity = MorphableCurve.preset().opacity * this._style.opacity;

        // Instanzen einmalig anlegen. LinesAA hält vorallokierte GPU-Puffer, deshalb
        // wird hier pro Frame nur noch hochgeladen — vorher entstand bei jedem Frame
        // der Morph-Animation eine neue Geometrie samt Materialien.
        if (!this._linesAA) {
            // Halo kommt aus dem Preset (bold hat einen, subtle bewusst nicht) —
            // der Tower-Look lebt von Zurückhaltung.
            const halo = MorphableCurve.preset().halo;
            if (halo) {
                this._haloAA = MorphableCurve.makeLines(
                    this._vertexCount, halo.opacity * this._style.opacity, width * halo.widthFactor);
                this._haloAA.mesh.name = 'MorphableCurveHalo';
                this._haloAA.mesh.renderOrder = -3;
                this._curveGroup.add(this._haloAA.mesh);
            }
            this._linesAA = MorphableCurve.makeLines(this._vertexCount, coreOpacity, width);
            this._linesAA.mesh.name = 'MorphableCurveMesh';
            // Vor den transparenten POI-Meshes zeichnen: Freisteller-Tiles schreiben
            // keine Tiefe, ein spaeter gemalter Strich laege sonst optisch VOR ihnen,
            // obwohl er raeumlich dahinter verlaeuft (Befund flora-hex #119655).
            this._linesAA.mesh.renderOrder = -2;
            this._curveGroup.add(this._linesAA.mesh);
        } else {
            // Stiländerungen über setStyle() durchreichen, ohne neu zu allokieren.
            this._linesAA.opacity = coreOpacity;
            this._linesAA.setWidthPx(width);
            const halo = MorphableCurve.preset().halo;
            if (this._haloAA && halo) {
                this._haloAA.opacity = halo.opacity * this._style.opacity;
                this._haloAA.setWidthPx(width * halo.widthFactor);
            }
        }

        // Polyline -> ein Segment je Punktpaar. thickness bleibt 1, die Breite
        // steuert baseRadius (siehe radiusForWidth).
        const segments: LineSegment[] = [];
        for (let i = 0; i < currentPositions.length - 1; i++) {
            segments.push({
                start: currentPositions[i],
                end: currentPositions[i + 1],
                startColor: rgb,
                endColor: rgb,
                thickness: 1,
            });
        }
        this._linesAA.setLines(segments);
        this._haloAA?.setLines(segments);

        // Reposition markers to follow the currently rendered polyline
        this.positionMarkers();
        //console.log(`🔄 MorphableCurve: Geometry updated with ${currentPositions.length} vertices`);
    }
    
    /**
     * Generate smooth curve from control points
     */
    private generateSmoothCurve(controlPoints: Vector3[]): Vector3[] {
        // For now, use simple Catmull-Rom spline
        // Later we can add bezier, b-spline, etc.
        
        if (controlPoints.length === 2) {
            // Simple line interpolation
            return this.interpolateLine(controlPoints[0], controlPoints[1], 32);
        }
        
        // Multi-point smooth curve
        const threePoints = controlPoints.map(p => new THREE.Vector3(p.x, p.y, p.z));
        const curve = new THREE.CatmullRomCurve3(threePoints, false, 'centripetal');
        
        // Sample curve at high resolution
        const samples = Math.max(64, controlPoints.length * 16);
        const curvePoints: Vector3[] = [];
        
        for (let i = 0; i <= samples; i++) {
            const t = i / samples;
            const point = curve.getPoint(t);
            curvePoints.push(new Vector3(point.x, point.y, point.z));
        }
        
        return curvePoints;
    }
    
    /**
     * Resample curve to exact vertex count for morphing
     */
    private resampleCurve(curvePoints: Vector3[], targetCount: number): Vector3[] {
        if (curvePoints.length === targetCount) return curvePoints;
        
        const result: Vector3[] = [];
        const totalLength = this.calculateCurveLength(curvePoints);
        
        for (let i = 0; i < targetCount; i++) {
            const t = i / (targetCount - 1);
            const targetDistance = t * totalLength;
            const point = this.getPointAtDistance(curvePoints, targetDistance);
            result.push(point);
        }
        
        return result;
    }
    
    // Alle lebenden LinesAA-Instanzen. Sie brauchen bei Resize ein setResolution();
    // die Umrechnung auf konstante CSS-Pixel macht seit three-kit 0.1.1 der Core.
    private static _instances: Set<LinesAA> = new Set();
    private static _resizeHooked = false;

    /**
     * Linien-Bildsprache. `tower` übernimmt die Gestaltung des ark-Tower-Diagramms
     * (Wave, Repo Arcturian, ArkTowerRenderer.tsx): die Verbindung ist ein zurück-
     * tretendes Strukturelement, keine Hauptfigur — dünn, halbtransparent, ohne Halo.
     * Gerendert wird über LinesAA aus @arkturian/three-kit (Waves Port von Alex'
     * LineGeometryShader.fx). Dessen Vertex-Shader kompensiert die perspektivische
     * Division per `pos.xy += offset * pos.w` — die Breite bleibt also screen-space-
     * stabil, die Haarlinie aus #785 kommt nicht zurück.
     * Die Werte stehen als LINE_PRESETS im Package (subtle/bold).
     *
     * Default ist `bold`. Waves `subtle` (1,4px bei 0,42 Deckkraft) ist fuer dunkle
     * Linien auf HELLEM Grund entworfen — dort tritt Grau zurueck. Die Praesentation
     * zeigt aber eine helle Linie auf sattem Rot; da verschwindet halbtransparentes
     * Creme fast vollstaendig (Befund Alex, 2026-08-07). Auf gesaettigtem Grund
     * traegt `bold` mit voller Deckkraft und Halo.
     * Umschaltbar per ?linestyle=subtle|bold.
     */
    private static lineStyleVariant(): 'subtle' | 'bold' {
        try {
            const v = new URLSearchParams(window.location.search).get('linestyle');
            // 'tower' bleibt als Alias auf subtle bestehen (frueher Vergleichs-Link).
            if (v === 'subtle' || v === 'tower') return 'subtle';
            return 'bold';
        } catch { return 'bold'; }
    }

    /** Aktives Preset aus dem Package. */
    private static preset() {
        return LINE_PRESETS[MorphableCurve.lineStyleVariant()];
    }

    /**
     * Ziel-Pixelbreite aus dem Preset — bewusst unabhängig von `style.thickness`.
     *
     * `thickness` ist hier kein verlässliches Maß: Layout.ts setzt es an einer Stelle
     * auf 0.02 (Kommentar dort: '= 2 Pixel'), an einer anderen auf 0.15 — Faktor 7,5
     * für dieselbe Kurve. Der sichtbare Regler `processCurveThickness` hängt zudem an
     * ProcessCurve, nicht an dieser Klasse. Eine Kopplung würde die Breite also von
     * einem widersprüchlichen Wert abhängig machen, den niemand steuert.
     * Die Preset-Nennbreite ist vorhersagbar; wird der Regler später wirklich
     * verdrahtet, gehört die Skalierung an genau dieser Stelle wieder hinein.
     */
    private static screenWidthPx(_thickness?: number): number {
        const p = MorphableCurve.preset();
        const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
        return isMobile ? p.widthPxMobile : p.widthPxDesktop;
    }

    /**
     * Legt eine LinesAA-Instanz an. `widthPx` hält die Breite in CSS-Pixeln konstant —
     * der Core rechnet baseRadius bei jedem setResolution() nach.
     */
    private static makeLines(maxLines: number, opacity: number, widthPx: number): LinesAA {
        const lines = new LinesAA({
            maxLines,
            widthPx,
            opacity,
            depthTest: true,
            depthWrite: false,
            blending: THREE.NormalBlending,
        });
        MorphableCurve._instances.add(lines);
        MorphableCurve.applyViewport(lines);
        MorphableCurve.hookResize();
        return lines;
    }

    /** setResolution erwartet CSS-Pixel — das DPR kürzt sich in der Core-Umrechnung heraus. */
    private static applyViewport(lines: LinesAA): void {
        if (typeof window === 'undefined') return;
        lines.setResolution(window.innerWidth, window.innerHeight);
    }

    private static hookResize(): void {
        if (MorphableCurve._resizeHooked || typeof window === 'undefined') return;
        MorphableCurve._resizeHooked = true;
        window.addEventListener('resize', () => {
            for (const lines of MorphableCurve._instances) MorphableCurve.applyViewport(lines);
        });
    }
    
    /**
     * Interpolate between two points
     */
    private interpolateLine(start: Vector3, end: Vector3, samples: number): Vector3[] {
        const points: Vector3[] = [];
        for (let i = 0; i <= samples; i++) {
            const t = i / samples;
            const point = start.clone().lerp(end, t);
            points.push(point);
        }
        return points;
    }
    
    /**
     * Calculate total curve length
     */
    private calculateCurveLength(points: Vector3[]): number {
        let length = 0;
        for (let i = 1; i < points.length; i++) {
            length += points[i].distanceTo(points[i - 1]);
        }
        return length;
    }
    
    /**
     * Get point at specific distance along curve
     */
    private getPointAtDistance(points: Vector3[], targetDistance: number): Vector3 {
        let currentDistance = 0;
        
        for (let i = 1; i < points.length; i++) {
            const segmentLength = points[i].distanceTo(points[i - 1]);
            
            if (currentDistance + segmentLength >= targetDistance) {
                // Interpolate within this segment
                const segmentProgress = (targetDistance - currentDistance) / segmentLength;
                return points[i - 1].clone().lerp(points[i], segmentProgress);
            }
            
            currentDistance += segmentLength;
        }
        
        // Return last point if distance exceeds curve length
        return points[points.length - 1].clone();
    }

    /**
     * Extend control points with pre/post offsets
     */
    private extendPathWithOffsets(controlPoints: Vector3[], preOffset: number, postOffset: number): Vector3[] {
        if (controlPoints.length < 2 || (preOffset === 0 && postOffset === 0)) {
            return controlPoints.slice();
        }
        const first = controlPoints[0];
        const second = controlPoints[1];
        const last = controlPoints[controlPoints.length - 1];
        const beforeLast = controlPoints[controlPoints.length - 2];
        
        const startDir = second.clone().subtract(first).normalize();
        const endDir = last.clone().subtract(beforeLast).normalize();
        
        const startPoint = first.clone().subtract(startDir.multiply(preOffset));
        const endPoint = last.clone().add(endDir.multiply(postOffset));
        
        const extended: Vector3[] = [startPoint, ...controlPoints.map(p => p.clone()), endPoint];
        return extended;
    }

    /**
     * Update number/start/end markers
     * - extendedPoints: controlPoints including optional offset points (for S/E)
     * - anchorPoints: original POI points (for numbering)
     */
    private buildMarkers(): void {
        if (!this._scene) return;
        // Build or rebuild sprites to match anchor points
        // Start sprite
        if (this._markerStyle.showStartMarker) {
            if (!this._startSprite) {
                this._startSprite = this.createTextSprite(this._markerStyle.startText);
                this._markersGroup.add(this._startSprite);
            }
        } else if (this._startSprite) {
            this._markersGroup.remove(this._startSprite);
            this._startSprite = null;
        }

        // Number sprites
        const targetCount = this._markerStyle.showNumbers ? this._lastAnchorPoints.length : 0;
        // Remove excess
        while (this._numberSprites.length > targetCount) {
            const s = this._numberSprites.pop()!;
            this._markersGroup.remove(s);
        }
        // Add missing
        while (this._numberSprites.length < targetCount) {
            const index = this._numberSprites.length;
            const sprite = this.createTextSprite(String(index + 1));
            this._markersGroup.add(sprite);
            this._numberSprites.push(sprite);
        }

        // End sprite
        if (this._markerStyle.showEndMarker) {
            if (!this._endSprite) {
                this._endSprite = this.createTextSprite(this._markerStyle.endText);
                this._markersGroup.add(this._endSprite);
            }
        } else if (this._endSprite) {
            this._markersGroup.remove(this._endSprite);
            this._endSprite = null;
        }
    }

    private positionMarkers(): void {
        if (!this._scene) return;
        // Current rendered polyline points
        const polyline: Vector3[] = this._vertices.map(v => (v.value || Vector3.zero()).clone());
        if (polyline.length < 2) return;

        // Start/end sprites follow extended polyline
        if (this._startSprite && this._lastExtendedPoints.length > 0) {
            const p = this._lastExtendedPoints[0];
            this._startSprite.position.set(p.x, p.y, p.z);
        }
        if (this._endSprite && this._lastExtendedPoints.length > 0) {
            const p = this._lastExtendedPoints[this._lastExtendedPoints.length - 1];
            this._endSprite.position.set(p.x, p.y, p.z);
        }

        // Numbers: project each anchor to polyline, then move along polyline towards prev/next by a fraction of the polyline distance to that neighbor
        const lineOffset = this._markerStyle.lineOffset ?? 0;
        const { cumLengths, totalLength } = this.buildArcLengthTable(polyline);
        for (let i = 0; i < this._numberSprites.length; i++) {
            const anchor = this._lastAnchorPoints[i];
            const curProj = this.projectPointOntoPolyline(polyline, anchor);
            let sCur = cumLengths[curProj.segIndex] + curProj.t * polyline[curProj.segIndex].distanceTo(polyline[curProj.segIndex + 1]);

            // Determine neighbor anchor based on sign
            const neighborIndex = lineOffset >= 0 ? Math.min(i + 1, this._lastAnchorPoints.length - 1) : Math.max(i - 1, 0);
            const neighbor = this._lastAnchorPoints[neighborIndex];
            const nbrProj = this.projectPointOntoPolyline(polyline, neighbor);
            const sNbr = cumLengths[nbrProj.segIndex] + nbrProj.t * polyline[nbrProj.segIndex].distanceTo(polyline[nbrProj.segIndex + 1]);

            const delta = Math.abs(sNbr - sCur);
            const sTarget = sCur + Math.sign(lineOffset) * (Math.abs(lineOffset) * delta);
            const pos = this.sampleAtArcLength(polyline, cumLengths, sTarget);
            this._numberSprites[i].position.set(pos.x, pos.y, pos.z);
        }
    }

    private projectPointOntoPolyline(polyline: Vector3[], target: Vector3): { point: Vector3; segIndex: number; t: number } {
        let closestPoint = polyline[0].clone();
        let closestDist = Number.POSITIVE_INFINITY;
        let closestSeg = 0;
        let closestT = 0;
        for (let i = 0; i < polyline.length - 1; i++) {
            const a = polyline[i];
            const b = polyline[i + 1];
            const { point: proj, t } = this.projectPointOnSegmentWithT(a, b, target);
            const d = proj.distanceTo(target);
            if (d < closestDist) {
                closestDist = d;
                closestPoint = proj;
                closestSeg = i;
                closestT = t;
            }
        }
        return { point: closestPoint, segIndex: closestSeg, t: closestT };
    }

    private projectPointOnSegmentWithT(a: Vector3, b: Vector3, p: Vector3): { point: Vector3; t: number } {
        const ab = b.subtract(a);
        const ap = p.subtract(a);
        const abLen2 = ab.lengthSquared();
        if (abLen2 === 0) return { point: a.clone(), t: 0 };
        const t = Math.max(0, Math.min(1, ap.dot(ab) / abLen2));
        return { point: a.lerp(b, t), t };
    }

    private buildArcLengthTable(polyline: Vector3[]): { cumLengths: number[]; totalLength: number } {
        const cum: number[] = [0];
        let acc = 0;
        for (let i = 1; i < polyline.length; i++) {
            acc += polyline[i - 1].distanceTo(polyline[i]);
            cum.push(acc);
        }
        return { cumLengths: cum, totalLength: acc };
    }

    private sampleAtArcLength(polyline: Vector3[], cumLengths: number[], s: number): Vector3 {
        if (s <= 0) return polyline[0].clone();
        const total = cumLengths[cumLengths.length - 1];
        if (s >= total) return polyline[polyline.length - 1].clone();
        // find segment
        let i = 0;
        while (i < cumLengths.length - 1 && cumLengths[i + 1] < s) i++;
        const segStartS = cumLengths[i];
        const segLen = polyline[i].distanceTo(polyline[i + 1]);
        const t = segLen > 0 ? (s - segStartS) / segLen : 0;
        return polyline[i].lerp(polyline[i + 1], t);
    }

    /**
     * Create a billboard sprite with text using canvas
     */
    /**
     * Ziffer als Billboard-Sprite.
     *
     * Bewusst OHNE Hintergrundplakette: die vorherige Fassung malte einen Kreis in
     * der Linienfarbe und setzte eine schwarze Arial-Ziffer darauf — auf sattem
     * Grund wirkte das wie ein aufgeklebtes Etikett statt wie Teil der Grafik.
     * Waves Bildsprache (LINE_PRESETS): Strukturelemente sind EINE gedämpfte Farbe,
     * die Ziffer trägt sich über Größe und Form, nicht über einen Träger.
     *
     * Der Canvas wird mit devicePixelRatio überabgetastet — ohne das war die Ziffer
     * auf Retina-Displays sichtbar unscharf (gleiche Klasse Fehler wie #785).
     */
    private createTextSprite(text: string): THREE.Sprite {
        const size = this._markerStyle.fontSizePx;
        const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 3) : 1;
        const padding = Math.floor(size * 0.45);
        const cssW = size + padding * 2;
        const cssH = size + padding * 2;

        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(cssW * dpr);
        canvas.height = Math.ceil(cssH * dpr);
        const ctx = canvas.getContext('2d')!;
        ctx.scale(dpr, dpr);

        const c = this._markerStyle.numberColor;
        const rgb = `${Math.round(c.r * 255)}, ${Math.round(c.g * 255)}, ${Math.round(c.b * 255)}`;

        ctx.font = `${this._markerStyle.fontWeight} ${size}px ${this._markerStyle.fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Weicher dunkler Schein statt Plakette: hält die Ziffer auf hellen wie
        // dunklen Flächen lesbar, ohne eine sichtbare Kante zu setzen.
        ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
        ctx.shadowBlur = Math.max(4, size * 0.18);
        ctx.shadowOffsetY = Math.max(1, size * 0.03);

        ctx.fillStyle = `rgba(${rgb}, 1)`;
        ctx.fillText(text, cssW / 2, cssH / 2);

        const texture = new THREE.Texture(canvas);
        texture.needsUpdate = true;
        texture.minFilter = THREE.LinearFilter;   // kein Mipmap-Matsch bei NPOT-Canvas
        texture.magFilter = THREE.LinearFilter;
        texture.generateMipmaps = false;
        if ('colorSpace' in texture) (texture as any).colorSpace = THREE.SRGBColorSpace;

        const material = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: true, depthWrite: false });
        const sprite = new THREE.Sprite(material);
        const worldSize = this._markerStyle.markerSize;
        sprite.scale.set(worldSize, worldSize, 1);
        sprite.renderOrder = 1000;
        return sprite;
    }

    private getOffsetMarkerPosition(index: number, points: Vector3[], lineOffset: number): Vector3 {
        if (!points || points.length === 0 || lineOffset === 0) {
            return points[index].clone();
        }
        const position = points[index];
        let target: Vector3 | null = null;
        if (lineOffset > 0) {
            // towards next, use prev if last
            if (index < points.length - 1) target = points[index + 1];
            else if (index > 0) target = points[index - 1];
        } else {
            // towards prev, use next if first
            if (index > 0) target = points[index - 1];
            else if (index < points.length - 1) target = points[index + 1];
        }
        if (!target) return position.clone();
        const dir = target.subtract(position).normalize();
        const offsetDist = Math.abs(lineOffset) * position.distanceTo(target);
        return position.add(dir.multiply(offsetDist));
    }
    
    /**
     * Show/hide curve
     */
    setVisible(visible: boolean): void {
        this._visible = visible;
        this._curveGroup.visible = visible;
    }
    
    /**
     * Update animation (call from main loop) - now handled by internal animation loop
     */
    update(deltaTime: number): void {
        // Animation updates are now handled internally by startAnimationUpdates()
        // This method is kept for compatibility but no longer needed
    }
    
    /**
     * Cleanup resources
     */
    dispose(): void {
        // Stop any running animations
        this.stopAnimationUpdates();
        
        for (const lines of [this._linesAA, this._haloAA]) {
            if (!lines) continue;
            this._curveGroup.remove(lines.mesh);
            MorphableCurve._instances.delete(lines);
            lines.dispose();
        }
        this._linesAA = null;
        this._haloAA = null;

        
        if (this._scene) {
            this._scene.remove(this._curveGroup);
        }
    }
}